// What a session runs and where, for lists (the sidebar, the palette, the dashboard's
// Activity): a small monochrome glyph per coding agent, the folder (~/…) and its git branch,
// and how long ago it started and was last busy. The session row is the same on every
// platform (spec "Session rows"): the agent's icon carrying the state, the name and how long
// ago; under it program · folder · branch, or why it needs you.

import type { ReactNode } from 'react';
import type { Session } from '../net/protocol';
import type { Organize } from './organize';
import { WAITING_LINE, waitingAsk } from './waiting';
import { agoPhrase, secondsSince, stateText, stateWords, useNow } from './time';

// Letters drawn as strokes (5 units tall, centred in a 12×12 box) for the agents without a
// symbol of their own.
const K = <path d="M4.8 3.5v5M7.4 3.5 4.8 6.1l2.6 2.4" />;
const circled = (letter: ReactNode) => (
  <>
    <circle cx="6" cy="6" r="5" />
    {letter}
  </>
);

/** One glyph per coding agent the machine recognises (agent resumers.go), 12×12, stroked. */
const GLYPHS: Record<string, ReactNode> = {
  'Claude Code': <path d="M6 1.5v9M2.1 3.75l7.8 4.5M2.1 8.25l7.8-4.5" />,
  Codex: <path d="M6 1.2 10.2 3.6v4.8L6 10.8 1.8 8.4V3.6Z" />,
  OpenCode: <path d="M4 3.5 1.5 6 4 8.5M8 3.5 10.5 6 8 8.5M6.9 2.5 5.1 9.5" />,
  Pi: circled(<path d="M3.7 4.4h4.6M5.1 4.4v4.1M6.9 4.4v3.3c0 .6.3.8.9.8" />),
  'Gemini CLI': <path d="M6 1c.4 2.8 2.2 4.6 5 5-2.8.4-4.6 2.2-5 5-.4-2.8-2.2-4.6-5-5 2.8-.4 4.6-2.2 5-5Z" />,
  'Copilot CLI': (
    <>
      <circle cx="3.6" cy="6.6" r="2.1" />
      <circle cx="8.4" cy="6.6" r="2.1" />
      <path d="M5.7 6.3c.2-.3.4-.4.3-.4s.1 0 .3.4M1.5 5.6 1 4.2M10.5 5.6l.5-1.4" />
    </>
  ),
  'Cursor Agent': <path d="M2.8 1.8v8.4l2.4-2.3 1.6 3.3 1.5-.7-1.6-3.2h3.3Z" />,
  Amp: <path d="M7 1 2.6 6.8H6L5 11l4.4-5.8H6Z" />,
  'Qwen Code': circled(<path d="M6 3.6a2.2 2.2 0 1 0 0 4.4 2.2 2.2 0 0 0 0-4.4ZM6.9 7.4l1.3 1.3" />),
  Crush: <path d="m6 1 1.1 2.6L9.5 2.5 8.4 4.9 11 6l-2.6 1.1 1.1 2.4-2.4-1.1L6 11 4.9 8.4 2.5 9.5l1.1-2.4L1 6l2.6-1.1-1.1-2.4 2.4 1.1Z" />,
  Kilo: circled(K),
  Droid: (
    <>
      <circle cx="6" cy="6" r="1.6" />
      <path d="M6 1.3v1.6M6 9.1v1.6M1.3 6h1.6M9.1 6h1.6M2.7 2.7l1.1 1.1M8.2 8.2l1.1 1.1M2.7 9.3l1.1-1.1M8.2 3.8l1.1-1.1" />
      <circle cx="6" cy="6" r="3.4" />
    </>
  ),
  Goose: <path d="M8.6 2.2c-1 0-1.6.7-1.6 1.6 0 1.4-1.2 2.2-2.6 2.4L1.5 6.6c.4 2.2 2.2 3.4 4.4 3.4 2.4 0 3.8-1.6 3.8-3.6V4.1l1.3-.5-1.2-.5c-.2-.5-.6-.9-1.2-.9Z" />,
  Cline: circled(<path d="M7.7 4.3a2.4 2.4 0 1 0 0 3.4" />),
  Kiro: (
    <>
      <rect x="1" y="1" width="10" height="10" rx="2.6" />
      {K}
    </>
  ),
  Aider: circled(<path d="M4.1 8.5 6 3.5l1.9 5M4.8 6.9h2.4" />),
};

const TERMINAL = (
  <>
    <rect x="1" y="2" width="10" height="8" rx="1.8" />
    <path d="M3.5 4.8 5 6 3.5 7.2M6.3 7.3h2.2" />
  </>
);

/** What a session runs, as a glyph: the agent's own, else a terminal. */
export function ProgramGlyph({ program, size = 12 }: { program?: string; size?: number }) {
  return (
    <svg
      className="glyph"
      width={size}
      height={size}
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.1"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      data-program={(program && program in GLYPHS && program) || 'terminal'}
    >
      {(program && GLYPHS[program]) || TERMINAL}
    </svg>
  );
}

export function BranchGlyph() {
  return (
    <svg className="glyph" width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" aria-hidden="true">
      <circle cx="2.8" cy="2.2" r="1.1" />
      <circle cx="2.8" cy="7.8" r="1.1" />
      <circle cx="7.2" cy="3.2" r="1.1" />
      <path d="M2.8 3.3v3.4M7.2 4.3c0 1.6-1.4 2-4.2 2.4" />
    </svg>
  );
}

/** "Claude Code · ~/projects/cmx · main" as text (a hint, a tooltip); '' before the machine says. */
export function sessionPlace(s: Session) {
  return s.row?.place ?? '';
}

/** A session's icon: its agent's glyph on a small tile, with a badge for its state (working,
 *  needs you, done, idle, exited); the whole tile turns amber when it needs you. A closed
 *  session (Recently closed) has no state: no badge. */
export function SessionIcon({ s }: { s: Pick<Session, 'program'> & { status?: Session['status'] } }) {
  const words = s.status ? STATUS_WORDS[s.status] : 'closed';
  return (
    <span className={`session-icon status-${s.status ?? 'closed'}`} data-testid="session-icon" data-status={s.status ?? 'closed'} role="img" aria-label={`${s.program || 'shell'}, ${words}`}>
      <ProgramGlyph program={s.program} size={12} />
      {s.status && <span className="session-badge" aria-hidden="true" />}
    </span>
  );
}

export const STATUS_WORDS: Record<Session['status'], string> = { working: 'running', waiting: 'needs you', done: 'done', idle: 'idle', exited: 'exited' };

/** The Running spinner: an arc turning (still when the person asks for less motion). */
export function Spinner({ size = 10 }: { size?: number }) {
  return (
    <svg className="spinner" width={size} height={size} viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
      <circle cx="5" cy="5" r="3.8" opacity="0.25" />
      <path d="M5 1.2a3.8 3.8 0 0 1 3.8 3.8" />
    </svg>
  );
}

/** The row's right side, the same words on every platform (spec "Session rows"; the machine
 *  says them, proto/state.go): a spinner and Running, Needs you and how long, Done and how
 *  long, Idle and how long, Exit and the code; how long keeps counting (useNow). */
export function SessionState({ s }: { s: Session }) {
  const now = useNow();
  return (
    <span className={`session-state state-${s.status}`} data-testid="session-state">
      {s.status === 'working' && <Spinner />}
      {stateText(stateWords(s), now)}
    </span>
  );
}

/** A recently closed session's right side at now: "closed 3h ago". */
export function closedAgo(iso: string, now: number) {
  const secs = secondsSince(iso, now);
  return Number.isFinite(secs) ? `closed ${agoPhrase(secs)}` : 'closed';
}

/** The session row's second line: program · folder · branch, leaving out what its group's
 *  header says (by folder, only a subfolder); "Waiting on you" while it needs you. */
export function SessionMeta({ s, organize = 'none' }: { s: Pick<Session, 'row' | 'program'> & Partial<Pick<Session, 'status' | 'notice'>>; organize?: Organize }) {
  if (s.status === 'waiting') {
    // what it asked is the tooltip
    return (
      <span className="session-meta needs-you" data-testid="session-meta" data-program={s.program ?? ''} title={waitingAsk(s)}>
        <span>{WAITING_LINE}</span>
      </span>
    );
  }
  const { program, where, branch } = s.row?.meta[organize] ?? {}; // as the cloud machine wrote it for this grouping
  if (!program && !where && !branch) return null;
  return (
    <span className="session-meta" data-testid="session-meta" data-program={s.program ?? ''}>
      {program && <span className="meta-program">{program}</span>}
      {where && <span className="meta-folder">{where}</span>}
      {branch && (
        <span className="meta-branch">
          <BranchGlyph />
          {branch}
        </span>
      )}
    </span>
  );
}
